import { Button } from "@/components";
import { t } from "@/context/language";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { BrazilMap } from "./brazilMap";

type SuccessStoriesProps = {
  onCtaClick: () => void;
};

const AUTO_ADVANCE_MS = 10000;

export function SuccessStories({ onCtaClick }: SuccessStoriesProps) {
  const stories = t.successStories.items;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % stories.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [activeIndex, isPaused, stories.length]);

  const goTo = (index: number) => {
    setIsPaused(true);
    setActiveIndex((index + stories.length) % stories.length);
  };

  const story = stories[activeIndex];

  return (
    <div className="grid min-h-screen items-center gap-12 py-24 lg:grid-cols-2 lg:gap-16">
      <motion.div
        className="space-y-5 text-left lg:space-y-7"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.h2
          className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
          style={{ fontFamily: "var(--font-merriweather)" }}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          {t.successStories.eyebrow}
        </motion.h2>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            className="space-y-5 lg:space-y-7"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="flex">
              <div className="relative w-[151px] sm:w-[179px]">
                <img
                  src={story.logo}
                  alt={story.logoAlt}
                  className="h-[59px] w-[179px] object-contain brightness-0 invert"
                />
              </div>
            </div>

            <h3 className="text-title-sm sm:text-heading-sm lg:text-heading-xl text-white">
              {story.headline}
            </h3>

            <blockquote className="text-base text-gray-300 sm:text-lg">
              &ldquo;{story.quote}&rdquo;
            </blockquote>

            {story.name && (
              <p className="text-sm font-medium text-gray-400">
                — {story.name}
                {story.role ? `, ${story.role}` : ""}
              </p>
            )}
          </motion.div>
        </AnimatePresence>

        <motion.div
          className="flex items-center gap-6 pt-2"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <motion.div whileTap={{ scale: 0.97 }}>
            <Button
              className="h-12 w-auto rounded-full px-7 text-base"
              onClick={onCtaClick}
            >
              {t.successStories.cta}
            </Button>
          </motion.div>

          {stories.length > 1 && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label={t.successStories.prevAriaLabel}
                onClick={() => goTo(activeIndex - 1)}
                className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-gray-600 text-white transition hover:opacity-90"
              >
                <ArrowLeft className="size-4" />
              </button>
              <button
                type="button"
                aria-label={t.successStories.nextAriaLabel}
                onClick={() => goTo(activeIndex + 1)}
                className="flex size-10 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-gray-600 text-white transition hover:opacity-90"
              >
                <ArrowRight className="size-4" />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>

      <motion.div
        className="flex justify-center"
        initial={{ opacity: 0, scale: 0.8, y: 30 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="w-full max-w-sm sm:max-w-md lg:max-w-xl">
          <motion.div transition={{ duration: 0.3 }}>
            <BrazilMap />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
